import { motion } from 'framer-motion';
import Image from 'next/image';
import { memo, useCallback, useMemo } from 'react';

import ElementAnimation from '@components/animation/ElementAnimation';
import { IBanner } from '@type/banner';

function MasonryGallery({ banners = [] }: { banners?: IBanner[] }) {
  const { column1, column2, column3 } = useMemo(() => {
    return {
      column1: banners.filter((_, i) => i % 3 === 0),
      column2: banners.filter((_, i) => i % 3 === 1),
      column3: banners.filter((_, i) => i % 3 === 2),
    };
  }, [banners]);

  const renderColumn = useCallback((images: IBanner[], duration: number, reverse = false) => {
    const duplicated = [...images, ...images]; // Duplicate for infinite scroll effect

    return (
      <div className="flex-1 overflow-hidden h-[70vh]">
        <motion.div
          animate={{ y: reverse ? ['0%', '-50%'] : ['-50%', '0%'] }}
          transition={{ repeat: Infinity, duration, ease: 'linear' }}
          className="flex flex-col space-y-2 md:space-y-4"
        >
          {duplicated.map((img, index) => (
            <div
              key={`${img._id}-${index}-${duration}`}
              className="cursor-pointer relative overflow-hidden rounded md:rounded-md bg-gradient-to-br from-purple-900 to-pink-900 shadow-2xl transition-all duration-300"
            >
              <Image
                src={img.image || '/placeholder.svg'}
                alt={img.title}
                width={600}
                height={800}
                className="w-full h-auto object-cover transition-transform duration-300"
                sizes="(max-width: 768px) 100vw, 33vw"
                loading="lazy"
                quality={70}
              />
            </div>
          ))}
        </motion.div>
      </div>
    );
  }, []);

  return (
    <div className="py-8 md:py-16 bg-gradient-to-b from-blue-50/40 to-white-50/70">
      <div className="container-xl">
        <ElementAnimation className="text-center mb-3 md:mb-12 w-full">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight">Hình ảnh</h2>
          <p className="text-muted-foreground mt-4 text-sm md:text-base max-w-[500px] md:max-w-[700px] mx-auto">
            Bộ sưu tập hình ảnh nổi bật từ các dự án và sản phẩm của chúng tôi
          </p>
        </ElementAnimation>

        <ElementAnimation className="flex gap-2 md:gap-4 rounded md:rounded-md overflow-hidden">
          {renderColumn(column1, 85, false)}
          {renderColumn(column2, 80, true)}
          {renderColumn(column3, 75, false)}
        </ElementAnimation>
      </div>
    </div>
  );
}

export default memo(MasonryGallery);
